import { toast as sonnerToast } from "sonner";
import type { ReactNode } from "react";

type ToastVariant = "default" | "destructive" | "success" | "warning" | "info" | null | undefined;

interface ToastInput {
  title?: ReactNode;
  description?: ReactNode;
  variant?: ToastVariant;
}

/**
 * Sonner-backed toast with the legacy shadcn call shape:
 *   `toast({ title, description, variant })`.
 *
 * `variant: "destructive"` → error toast; other variants map to their sonner
 * kinds; absent/default → a plain toast (so neutral messages don't get a
 * spurious success checkmark). Delegates to the `sonner` package; the
 * `<Toaster />` is mounted by `<GrootUIProvider>` from `@groot/ui`.
 */
export function toast({ title, description, variant }: ToastInput) {
  const message = title ?? description ?? "";
  const opts = description && title ? { description } : undefined;

  switch (variant) {
    case "destructive":
      return sonnerToast.error(message, opts);
    case "success":
      return sonnerToast.success(message, opts);
    case "warning":
      return sonnerToast.warning(message, opts);
    case "info":
      return sonnerToast.info(message, opts);
    default:
      return sonnerToast(message, opts);
  }
}
